import { useState } from 'react'
import { Info } from 'lucide-react'
import {
  CurrencyValue, EmptyState, ErrorState, FilterBar, PageHeader,
} from '@/components/common'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { usePayrollRuns } from '@/features/payroll'
import { Select } from '@/components/ui/select'
import {
  useBankSummary, useGpssaReport, usePaymentBatches, usePayrollRegister, useProvisionMovement,
} from '../api'

const TABS = ['register', 'bank', 'gpssa', 'provisions'] as const
type Tab = (typeof TABS)[number]

const TAB_LABELS: Record<Tab, string> = {
  register: 'Payroll register',
  bank: 'Bank summary',
  gpssa: 'GPSSA contributions',
  provisions: 'Provision movement',
}

/** The four end-of-run reports. All read what the run produced; none recomputes it. */
export function PayrollReportsPage() {
  useBreadcrumb()
  const [tab, setTab] = useState<Tab>('register')
  const now = new Date()
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const runs = usePayrollRuns()
  const batches = usePaymentBatches()
  const [runId, setRunId] = useState<string | null>(null)
  const [batchId, setBatchId] = useState<string | null>(null)

  const register = usePayrollRegister(tab === 'register' ? runId : null)
  const bank = useBankSummary(tab === 'bank' ? batchId : null)
  const gpssa = useGpssaReport(year, month)
  const provisions = useProvisionMovement(year, month)

  return (
    <div className="space-y-4">
      <PageHeader
        title="Payroll reports"
        subtitle="What a run paid, what the bank is asked to move, and what is provided for."
      />

      <div className="flex flex-wrap gap-2" role="tablist">
        {TABS.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={tab === option}
            className={`rounded-md border px-3 py-1.5 text-sm ${
              tab === option
                ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-white'
                : 'border-slate-300 text-slate-700'
            }`}
            onClick={() => setTab(option)}
          >
            {TAB_LABELS[option]}
          </button>
        ))}
      </div>

      {tab === 'register' && (
        <>
          <Card className="p-4">
            <Label htmlFor="report-run">Payroll run</Label>
            <Select
              id="report-run"
              className="mt-1 w-full max-w-md"
              value={runId ?? ''}
              onChange={(event) => setRunId(event.target.value || null)}
            >
              <option value="">Choose a run…</option>
              {runs.data?.map((run) => (
                <option key={run.id} value={run.id}>
                  {run.periodStart} → {run.periodEnd} ({run.status})
                </option>
              ))}
            </Select>
          </Card>

          {runId == null && (
            <EmptyState
              title="Choose a run"
              description="The register lists every person and every element in one run."
            />
          )}
          {register.isPending && runId != null && <Skeleton className="h-40" />}
          {register.isError && (
            <ErrorState error={register.error} onRetry={() => void register.refetch()} />
          )}
          {register.isSuccess && (
            <Card>
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Person</th>
                      {register.data.columns.map((column) => (
                        <th key={column.elementId} className="p-3 text-end font-medium">
                          {column.name}
                          {column.deduction && (
                            <span className="block text-xs font-normal">deduction</span>
                          )}
                        </th>
                      ))}
                      <th className="p-3 text-end font-medium">Net</th>
                    </tr>
                  </thead>
                  <tbody>
                    {register.data.rows.length === 0 && (
                      <tr>
                        <td className="p-4 text-slate-600"
                            colSpan={register.data.columns.length + 2}>
                          This run calculated nothing. No pay element is configured, so there
                          is nothing to list.
                        </td>
                      </tr>
                    )}
                    {register.data.rows.map((row) => (
                      <tr key={row.employeeId} className="border-b border-slate-100">
                        <td className="p-3">{row.employeeName ?? row.employeeCode}</td>
                        {row.amounts.map((amount, index) => (
                          <td key={register.data.columns[index]?.elementId ?? index}
                              className="p-3 text-end">
                            {amount}
                          </td>
                        ))}
                        <td className="p-3 text-end font-medium">{row.netPay}</td>
                      </tr>
                    ))}
                    {register.data.rows.length > 0 && (
                      <tr className="border-t border-slate-300 font-medium">
                        <td className="p-3">Total</td>
                        {register.data.columnTotals.map((total, index) => (
                          <td key={register.data.columns[index]?.elementId ?? index}
                              className="p-3 text-end">
                            {total}
                          </td>
                        ))}
                        <td className="p-3 text-end">{register.data.netTotal}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === 'bank' && (
        <>
          <Card className="p-4">
            <Label htmlFor="report-batch">Payment batch</Label>
            <Select
              id="report-batch"
              className="mt-1 w-full max-w-md"
              value={batchId ?? ''}
              onChange={(event) => setBatchId(event.target.value || null)}
            >
              <option value="">Choose a batch…</option>
              {batches.data?.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.reference} ({batch.periodStart} → {batch.periodEnd})
                </option>
              ))}
            </Select>
          </Card>

          {batchId == null && (
            <EmptyState
              title="Choose a batch"
              description="The summary is built from the batch, not the run — so it shows what the bank will actually receive."
            />
          )}
          {bank.isSuccess && (
            <>
              {bank.data.exclusionNotice && (
                <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                  <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
                  {bank.data.exclusionNotice}
                </p>
              )}
              <Card>
                <div className="overflow-x-auto overscroll-x-contain">
                  <table className="w-full text-sm">
                    <thead className="border-b border-slate-200 text-slate-600">
                      <tr>
                        <th className="p-3 text-start font-medium">Bank</th>
                        <th className="p-3 text-start font-medium">Routing</th>
                        <th className="p-3 text-end font-medium">Payments</th>
                        <th className="p-3 text-end font-medium">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bank.data.rows.length === 0 && (
                        <tr>
                          <td className="p-4 text-slate-600" colSpan={4}>
                            Nothing in this batch can be paid. Every line says why.
                          </td>
                        </tr>
                      )}
                      {bank.data.rows.map((row) => (
                        <tr key={row.bankName} className="border-b border-slate-100">
                          <td className="p-3">{row.bankName}</td>
                          <td className="p-3 font-mono text-xs text-slate-600">
                            {row.routingCode ?? '—'}
                          </td>
                          <td className="p-3 text-end">{row.payments}</td>
                          <td className="p-3 text-end">{row.total}</td>
                        </tr>
                      ))}
                      <tr className="border-t border-slate-300 font-medium">
                        <td className="p-3">Total</td><td /><td />
                        <td className="p-3 text-end">{bank.data.total}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </Card>
            </>
          )}
        </>
      )}

      {(tab === 'gpssa' || tab === 'provisions') && (
        <FilterBar>
          <Input
            aria-label="Year"
            id="report-year" type="number" className="w-28" value={year}
            onChange={(event) => setYear(Number(event.target.value))}
          />

          <Input
            aria-label="Month"
            id="report-month" type="number" min={1} max={12} className="w-20" value={month}
            onChange={(event) => setMonth(Number(event.target.value))}
          />
        </FilterBar>
      )}

      {tab === 'gpssa' && (
        <>
          {gpssa.isPending && <Skeleton className="h-32" />}
          {gpssa.isSuccess && gpssa.data.unavailableReason && (
            <p className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {gpssa.data.unavailableReason}
            </p>
          )}
          {gpssa.isSuccess && gpssa.data.rows.length > 0 && (
            <Card>
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Person</th>
                      <th className="p-3 text-start font-medium">Nationality</th>
                      <th className="p-3 text-end font-medium">Contributory wage</th>
                      <th className="p-3 text-end font-medium">Employee</th>
                      <th className="p-3 text-end font-medium">Employer</th>
                      <th className="p-3 text-end font-medium">Government</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gpssa.data.rows.map((row) => (
                      <tr key={row.employeeId} className="border-b border-slate-100">
                        <td className="p-3">{row.employeeName ?? row.employeeCode}</td>
                        <td className="p-3 text-slate-600">{row.nationality ?? '—'}</td>
                        <td className="p-3 text-end">{row.contributoryWage}</td>
                        <td className="p-3 text-end">{row.employeeAmount}</td>
                        <td className="p-3 text-end">{row.employerAmount}</td>
                        <td className="p-3 text-end">{row.governmentAmount}</td>
                      </tr>
                    ))}
                    <tr className="border-t border-slate-300 font-medium">
                      <td className="p-3">Total</td><td /><td />
                      <td className="p-3 text-end">{gpssa.data.employeeTotal}</td>
                      <td className="p-3 text-end">{gpssa.data.employerTotal}</td>
                      <td className="p-3 text-end">{gpssa.data.governmentTotal}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}

      {tab === 'provisions' && (
        <>
          {provisions.isPending && <Skeleton className="h-32" />}
          {provisions.isSuccess && provisions.data.length === 0 && (
            <EmptyState
              title="Nothing has been provided for this month"
              description="Provisions are written by the monthly accrual. Run it for this period and the movement appears here."
            />
          )}
          {provisions.isSuccess && provisions.data.length > 0 && (
            <Card>
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Provision</th>
                      <th className="p-3 text-end font-medium">People</th>
                      <th className="p-3 text-end font-medium">Opening</th>
                      <th className="p-3 text-end font-medium">Accrued</th>
                      <th className="p-3 text-end font-medium">Used</th>
                      <th className="p-3 text-end font-medium">Closing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {provisions.data.map((row) => (
                      <tr key={row.provisionType} className="border-b border-slate-100 align-top">
                        <td className="p-3">
                          {row.provisionType}
                          {row.basis && (
                            <p className="mt-1 max-w-md text-xs text-slate-600">{row.basis}</p>
                          )}
                        </td>
                        <td className="p-3 text-end">{row.employees}</td>
                        <td className="p-3 text-end">
                          <CurrencyValue value={row.opening} />
                        </td>
                        <td className="p-3 text-end"><CurrencyValue value={row.accrued} /></td>
                        <td className="p-3 text-end"><CurrencyValue value={row.utilised} /></td>
                        <td className="p-3 text-end font-medium">
                          <CurrencyValue value={row.closing} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  )
}
